import { __awaiter, __generator } from "tslib";
import { generateRequestEvent } from '@office-start/instrumentation-service';
import { now } from '@office-start/performance-utility';
import { logDiagnostic, logRequest } from '@office-start/telemetry-service';
import axios from 'axios';
import { axiosClient, preAxiosCacheInterceptor } from './axiosClient';
// TODO: Revisit caching strategy (custom indexdb or apollo)
var unknownError = 'NotAvailable';
var unknownStatusCode = -1;
/* Default timeout used in service calls initiated from office.com client.
 * Currently set at 5 min as our logs indicate that we have small number of successful calls exceeding 1 min.
 * TODO: This mostly happens for Upload API call so we might want to consider adding separate setting for Upload
 * and reducing this number to 1 min.
 */
var defaultTimeoutInMs = 300000;
function failedResponseWithCode(httpCode, fromCache, error) {
    return {
        data: undefined,
        status: httpCode,
        headers: {},
        ok: false,
        statusText: error && typeof error === 'string' ? error : httpCode.toString(),
        fromCache: fromCache,
    };
}
function getResponse(requestConfig, timeoutInMs, strategy, onUploadProgress, onDownloadProgress, cancellationToken) {
    var _this = this;
    return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
        var fromCache, axiosConfig, CancelToken, source_1, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    fromCache = strategy === "cache-only" /* CacheOnly */;
                    axiosConfig = {
                        url: requestConfig.url,
                        method: requestConfig.method,
                        headers: requestConfig.headers,
                        params: requestConfig.params,
                        data: requestConfig.body,
                        timeout: timeoutInMs,
                        responseType: requestConfig.responseType,
                        onUploadProgress: onUploadProgress,
                        onDownloadProgress: onDownloadProgress,
                    };
                    if (cancellationToken) {
                        CancelToken = axios.CancelToken;
                        source_1 = CancelToken.source();
                        axiosConfig.cancelToken = source_1.token;
                        cancellationToken.onCancel(function () { return source_1.cancel(); });
                    }
                    if (!preAxiosCacheInterceptor) return [3 /*break*/, 4];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, preAxiosCacheInterceptor(axiosConfig, strategy || "network-only" /* NetworkOnly */)];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    reject(failedResponseWithCode(501 /* NotImplemented */, fromCache, error_1));
                    return [2 /*return*/];
                case 4:
                    axiosClient.request(axiosConfig).then(function (result) {
                        resolve({
                            data: result.data,
                            status: result.status,
                            headers: result.headers,
                            ok: true,
                            statusText: result.statusText,
                            fromCache: fromCache,
                        });
                    }, function (error) {
                        if (axios.isCancel(error)) {
                            reject(failedResponseWithCode(705 /* Cancelled */, fromCache));
                        }
                        else if (error.code === 'ECONNABORTED') {
                            reject(failedResponseWithCode(707 /* Timeout */, fromCache));
                        }
                        else if (error.response) {
                            reject({
                                data: error.response.data,
                                status: error.response.status,
                                headers: error.response.headers,
                                ok: false,
                                statusText: error.response.statusText,
                                fromCache: false,
                            });
                        }
                        else if (window.navigator &&
                            typeof window.navigator.onLine === 'boolean' &&
                            !window.navigator.onLine) {
                            reject(failedResponseWithCode(706 /* Offline */, fromCache));
                        }
                        else {
                            reject(failedResponseWithCode(unknownStatusCode, fromCache));
                        }
                    });
                    return [2 /*return*/];
            }
        });
    }); });
}
// Instrument response
function instrumentResponse(instrumentedHttpDataResponse, eventName, start, populateRequestEvent) {
    // Do not instrument cache failure when cache is not supported
    if (instrumentedHttpDataResponse.fromCache &&
        !instrumentedHttpDataResponse.ok &&
        instrumentedHttpDataResponse.status === 501 /* NotImplemented */) {
        return;
    }
    var latency = Math.ceil(now() - start);
    // TODO: Validate whether this is needed
    var status = instrumentedHttpDataResponse.status;
    if (status === null || typeof status === 'undefined') {
        status = -2;
    }
    var statusText = instrumentedHttpDataResponse.statusText;
    var error;
    var errorText = statusText;
    if (statusText === null || typeof statusText === 'undefined') {
        error = unknownError;
        errorText = unknownError;
    }
    else if (statusText.toLowerCase() === 'ok') {
        error = statusText;
    }
    else {
        error = { value: statusText, kind: 2 /* GenericData */ };
    }
    var instrumentationParams = {};
    // Post-process event before recording
    if (populateRequestEvent) {
        populateRequestEvent(instrumentedHttpDataResponse, instrumentationParams);
    }
    // Check if there is property override of httpCode
    if (instrumentationParams.hasOwnProperty("HttpCode" /* HttpCode */)) {
        status = instrumentationParams["HttpCode" /* HttpCode */];
        delete instrumentationParams["HttpCode" /* HttpCode */];
    }
    logRequest({
        eventName: eventName,
        error: errorText,
        latency: latency,
        fromCache: instrumentedHttpDataResponse.fromCache,
        httpCode: status,
    });
    // log custom fields to OTEL Diagnostic events
    logDiagnostic({ eventName: eventName, params: instrumentationParams });
    // TODO: Remove following code once we migrate to OTEL
    var instrumentationEvent = generateRequestEvent(eventName, status, latency, error, instrumentedHttpDataResponse.fromCache, instrumentationParams);
    instrumentationEvent.record();
}
/**
 * Runs and instruments the result of execution of the given Http operation.
 *
 * @param eventName Name of the instrumentation event.
 * @param requestConfig The object which describes Http operation to run.
 * @param swConfig Configuration of requests meant for service worker.
 * @param populateRequestEvent Callback used to populate the telemetry event.
 * @param onUploadProgress A callback to receive upload progress events.
 * @param onDownloadProgress A callback to receive download progress events.
 * @param cancellationToken Token that allows cancellation of in-flight request.
 * @param timeoutInMs Timeout in milliseconds for the request.
 * @returns Promise object that will be completed asynchronously when Http operation is done
 * and its result is instrumented.
 * NOTE: This does not apply headers necessary for OfficeHome or Service worker.
 * This should only be used directly when making static resource fetches guaranteed not to hit OfficeHome.
 * Any scenarios that hit OfficeHome prod or mock apis will need to use requestWithOfficeContext()
 */
export function requestWithInstrumentation(eventName, requestConfig, strategy, populateRequestEvent, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs) {
    // Start measuring the latency
    var start = now();
    var fromCache = strategy === "cache-only" /* CacheOnly */;
    var responsePromise = getResponse(requestConfig, timeoutInMs || defaultTimeoutInMs, strategy, onUploadProgress, onDownloadProgress, cancellationToken);
    // TODO: Validate whether this is necessary with axios
    // Necessary to instrument the cancelled http request, since the promise never resolves
    var cancelTask = cancellationToken
        ? cancellationToken.onCancel(function () {
            return Promise.resolve(failedResponseWithCode(705 /* Cancelled */, fromCache));
        })
        : new Promise(function () {
            /*Never resolve*/
        });
    var instrumentRequest = function (response) {
        instrumentResponse(response, eventName, start, populateRequestEvent);
    };
    Promise.race([responsePromise, cancelTask]).then(instrumentRequest, instrumentRequest);
    return responsePromise;
}
//# sourceMappingURL=requestWithInstrumentation.js.map